<template>
    <b-col :id="rootId" cols="12" md="5">
        <div v-if="escriptReceivedDisplay" class="small mt-1 mb-2">
            <i class="fas fa-clock"></i> {{ escriptReceivedDisplay }}
        </div>

        <div v-if="requestReferenceNumber" class="small mt-1 mb-2">
            <i class="fas fa-hashtag"></i> Request Ref #: {{ requestReferenceNumber }}
        </div>

        <!-- Sticky image section -->
        <div class="image-wrapper">
            <ImageComponent ref="prescriptionImage" :escriptID="cancelRxEscriptID || shownEscriptID"
                :imageID="prescription.imageID" :rxNumber="prescription.rxNumber"
                class="script-image" />

            <!-- Typing/Verification Info -->
            <div class="typing-info mt-2">
                <div v-if="prescription.createdBy">
                    <b>Typed By</b>
                    <span class="name">{{ prescription.createdBy?.split('@')[0] }}</span>
                    <span class="date">{{ formattedDateAndTime(prescription.created) }}</span>
                </div>
                <div v-if="prescription.pV1ID">
                    <b>PV1 By</b>
                    <span class="name">{{ prescription.pv1By?.split('@')[0] }}</span>
                    <span class="date">{{ formattedDateAndTime(prescription.pv1) }}</span>
                </div>
                <div v-if="prescription.pV2ID">
                    <b>Verified By</b>
                    <span class="name">{{ prescription.pv2By?.split('@')[0] }}</span>
                    <span class="date">{{ formattedDateAndTime(prescription.pv2) }}</span>
                </div>
            </div>

            <HardCopyPrescriptionsComponent
                :imageID="prescription.imageID"
                :currentStoreID="prescription.storeID"
                :currentRxNumber="prescription.rxNumber"
                :currentRfNumber="prescription.rfNumber"
                :currentPatientID="prescription.patientID"
                :showCrossPatient="showCrossPatientHardCopyScripts"
                :hideSamePatient="hideSamePatientHardCopyScripts"
                @count-changed="onSiblingCountChanged"
            />
        </div>

        <!-- Add Image dropzone -->
        <b-modal :id="addImageModalId" title="Add Image" centered-title="xl">
            <b-row>
                <b-col>
                    <vue-dropzone id="dropzone" ref="myVueDropzone" :options="dropzoneOptions"
                        @vdropzone-success="dropzoneUploadSuccess" @vdropzone-sending="dropzoneSending"
                        @vdropzone-error="dropzoneUploadError" />
                </b-col>
            </b-row>
        </b-modal>

        <!-- PDF Annotation Modal -->
        <PdfAnnotationModal
            ref="pdf-annotation-modal"
            :image-id="annotationImageId"
            :escript-id="annotationEscriptId"
            :initial-text="quickAnnotateText"
            :auto-place="isQuickAnnotate"
            :sibling-count="siblingScriptCount"
            :positive-id-enabled="positiveIdEnabled"
            :positive-id-methods="positiveIdMethods"
            :positive-id-target-id="prescription && prescription.rxNumber ? prescription.rxID : null"
            :prescription-id="annotationPrescriptionId"
            :modal-id="pdfAnnotationModalId"
            title="Edit Prescription Image"
            :require-permission="true"
            :show-success-notification="false"
            @saved="onPdfAnnotationSaved"
            @closed="onPdfAnnotationClosed" />
    </b-col>
</template>

<script lang="ts">
// The two-column LEFT image panel, extracted from PrescriptionDetailsPage: image display,
// typed-by/PV1/PV2 info, the hardcopy-siblings list, plus the two page-level image modals (the
// add-image dropzone and PdfAnnotationModal).
//
// Boundary: every action button sits in the container's action row above this panel, so it can lay
// them out on one line; AnnotationRevertMixin, allowAnnotationRevert and the
// prescription.imageID watcher (originCode + sourceImageId + page count) stay there too. This panel
// takes read-only entity props and drives everything through scoped events + public @Ref methods;
// it never mutates the prescription — image changes emit `image-updated` and the container
// reassigns imageID.
//
// Events: image-updated({imageId}), note-consumed().
// Public methods (typed @Ref): openAddImage(), openAnnotator(), openQuickAnnotate(text),
//   refreshImage(imageId?).
import { Component, Vue, Prop, Ref } from "vue-property-decorator";
import Axios from "axios";
import { formatDateAndTime } from "@/util/DateUtils";
import Vue2Dropzone from "vue2-dropzone";
import { Prescription } from "@/models/Prescription";
import { Store, StoreTimeZone } from "@/models/Store";
import { Escript } from "@/models/Escript";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { PrescriptionService } from "@/services/PrescriptionService";
import ImageComponent from "@/components/Prescription/ImageComponent.vue";
import HardCopyPrescriptionsComponent from "@/components/Prescription/HardCopyPrescriptionsComponent.vue";
import PdfAnnotationModal from "@/components/PdfAnnotationModal.vue";

@Component({
    components: {
        ImageComponent,
        HardCopyPrescriptionsComponent,
        PdfAnnotationModal,
        VueDropzone: Vue2Dropzone,
    },
})
export default class RxImagePanel extends Vue {
    @Prop({ required: true }) prescription!: Prescription;
    @Prop({ default: null }) cancelRxEscriptID!: number | null;
    // The eScript to *display*, which can outlive the prescription's own link: after Save-and-New
    // the link is dropped so the server can't re-adopt the saved Rx number, but the draft should
    // keep showing the eScript it was typed from. Unset falls back to the link itself.
    @Prop({ default: null }) displayEscriptID!: number | null;
    @Prop({ default: false }) isCancelRxActive!: boolean;
    @Prop({ required: true }) store!: Store;
    @Prop({ default: null }) escript!: Escript | null;
    // The NCPDP Request Reference Number parsed from the escript message (Prescryptive transfers
    // carry it); shown under the received timestamp. Null/absent for escripts without one.
    @Prop({ default: null }) requestReferenceNumber!: string | null;
    @Prop({ default: false }) showCrossPatientHardCopyScripts!: boolean;
    @Prop({ default: false }) hideSamePatientHardCopyScripts!: boolean;
    @Prop({ default: false }) newRefillMode!: boolean;
    // Positive-ID config the annotation modal shares with the container's save flow. The
    // target-id is derived from the prescription (rxID once saved), matching the container's
    // original inline binding — not the container's positiveIdTargetId, which PositiveIdGate uses.
    @Prop({ default: false }) positiveIdEnabled!: boolean;
    @Prop({ default: null }) positiveIdMethods!: string[] | null;
    // Modal ids resolve globally in BootstrapVue and the root DOM id must be unique, so a second
    // panel instance on the same page namespaces both. Default null keeps the original ids, leaving
    // existing single-instance usage unchanged.
    @Prop({ default: null }) idPrefix!: string | null;

    @Ref('prescriptionImage') imageComponent!: ImageComponent;

    private quickAnnotateText: string | null = null;
    private isQuickAnnotate = false;
    private siblingScriptCount = 0;

    private prefixedId(id: string): string {
        return this.idPrefix ? `${this.idPrefix}-${id}` : id;
    }

    get rootId(): string {
        return this.prefixedId("prescription-image");
    }

    get addImageModalId(): string {
        return this.prefixedId("add-image");
    }

    get pdfAnnotationModalId(): string {
        return this.prefixedId("prescription-pdf-annotation");
    }

    get shownEscriptID(): number | null {
        return this.displayEscriptID || this.prescription?.escriptID || null;
    }

    // Annotation writes to the document it targets, so it follows the real link, never the display
    // fallback: stamping a note against a merely-displayed eScript would burn it onto the
    // prescription that still owns it.
    get annotationImageId(): number | null {
        // For escripts, imageId will be null - the modal will use escriptId instead
        // For regular prescriptions, use the imageID
        if (this.prescription?.escriptID) {
            return null;
        }
        return this.prescription?.imageID || null;
    }

    get annotationEscriptId(): number | null {
        return this.prescription?.escriptID || null;
    }

    // The Rx a hardcopy note can be filed against: only one that already exists as a saved row.
    // Mirrors the `linkedToRx` condition the annotated image itself is linked under, so the note and
    // the image are recorded on the same terms. In new-refill mode `rfNumber` is cleared while the
    // refill is being built, which makes `rxID` a fill-less `{store}-{rx}-` that matches no
    // prescription — filing a note under it would strand the note on an id nothing ever reads.
    get annotationPrescriptionId(): string | null {
        if (!this.prescription?.rxNumber || this.newRefillMode) return null;
        return this.prescription.rxID;
    }

    get escriptReceivedDisplay(): string | null {
        if (!this.escript?.created) return null;

        const date = new Date(this.escript.created);
        if (isNaN(date.getTime())) return null;

        const storeTimeZoneToIana: Record<number, string> = {
            [StoreTimeZone.PacificStandardTime]: "America/Los_Angeles",
            [StoreTimeZone.MountainStandardTime]: "America/Denver",
            [StoreTimeZone.USMountainStandardTime]: "America/Phoenix",
            [StoreTimeZone.CentralStandardTime]: "America/Chicago",
            [StoreTimeZone.EasternStandardTime]: "America/New_York",
            [StoreTimeZone.USEasternStandardTime]: "America/New_York",
        };

        const browserIana = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const storeIana = this.store.timeZone != null ? storeTimeZoneToIana[this.store.timeZone] : null;
        const primaryTz = storeIana || browserIana;

        const formatInTz = (tz: string, includeDate: boolean) => {
            const options: Intl.DateTimeFormatOptions = {
                timeZone: tz,
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                timeZoneName: "short",
            };
            if (includeDate) {
                options.year = "numeric";
                options.month = "long";
                options.day = "numeric";
            }
            const parts = new Intl.DateTimeFormat("en-US", options).formatToParts(date);
            const get = (type: string) => parts.find(p => p.type === type)?.value ?? "";
            const tzAbbrev = get("timeZoneName");
            if (includeDate) {
                return `${get("month")} ${get("day")}, ${get("year")} ${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")} (${tzAbbrev})`;
            }
            return `${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")} (${tzAbbrev})`;
        };

        const primaryDisplay = formatInTz(primaryTz, true);

        if (storeIana && storeIana !== browserIana) {
            const browserDisplay = formatInTz(browserIana, false);
            const storeTimeOnly = formatInTz(storeIana, false);
            if (storeTimeOnly !== browserDisplay) {
                return `Received: ${primaryDisplay} / ${browserDisplay}`;
            }
        }

        return `Received: ${primaryDisplay}`;
    }

    get dropzoneOptions(): any {
        const url =
            !this.prescription.rxNumber || this.newRefillMode
                ? `${Axios.defaults.baseURL}/image?bypassIngestion=true`
                : `${Axios.defaults.baseURL}/image/prescription/${this.prescription.storeID}/${this.prescription.rxNumber}/${this.prescription.rfNumber}`;
        return { url, thumbnailWidth: 150, withCredentials: true, paramName: "images" };
    }

    formattedDateAndTime(value: Date): string {
        return formatDateAndTime(value);
    }

    onSiblingCountChanged(count: number) {
        this.siblingScriptCount = count;
    }

    // ---- Public methods (typed @Ref from the container) --------------------------------------

    openAddImage() {
        this.$bvModal.show(this.addImageModalId);
    }

    openAnnotator() {
        // CancelRx images are display-only: annotating would target the NewRx escript
        // (since prescription.escriptID is preserved for pairing) and silently corrupt it.
        if (this.isCancelRxActive) {
            this.$notification(NotificationOptions.error(
                'CancelRx images are display-only and cannot be annotated.'
            ));
            return;
        }

        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to annotate."));
            return;
        }

        // Reset quick annotate state for manual annotation
        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;

        this.$bvModal.show(this.pdfAnnotationModalId);
    }

    openQuickAnnotate(noteText: string) {
        if (this.isCancelRxActive) {
            this.$notification(NotificationOptions.error(
                'CancelRx images are display-only and cannot be annotated.'
            ));
            return;
        }

        if (!this.prescription?.imageID && !this.prescription?.escriptID) {
            this.$notification(NotificationOptions.error("No image available to annotate."));
            return;
        }

        if (!noteText?.trim()) {
            this.$notification(NotificationOptions.error("Please enter a note to add."));
            return;
        }

        // Set up quick annotate with the note text
        this.quickAnnotateText = noteText.trim();
        this.isQuickAnnotate = true;

        this.$bvModal.show(this.pdfAnnotationModalId);
    }

    refreshImage(imageId?: number) {
        const id = imageId ?? this.prescription.imageID;
        if (this.imageComponent) {
            (this.imageComponent as any).fetchPrescriptionImageSource(`image/${id}`);
        }
    }

    // ---- Dropzone -----------------------------------------------------------------------------

    dropzoneSending(_file: any, xhr: XMLHttpRequest, _formData: FormData) {
        xhr.setRequestHeader("Authorization", Axios.defaults.headers["Authorization"]?.toString() ?? "");
    }

    dropzoneUploadSuccess(file: any, response: any) {
        const dropzone: any = this.$refs.myVueDropzone;
        // noinspection JSUnresolvedReference
        dropzone.removeFile(file);

        // pre-save upload returns number[]; the prescription endpoint returns a scalar id
        const imageId = Array.isArray(response) ? response[0] : response;
        if (imageId == null) {
            this.$bvModal.msgBoxOk("The upload completed but no image was returned. Please try again.", { title: "Upload failed" });
            return;
        }
        // The container owns entity mutation; emit and let it reassign prescription.imageID.
        this.$emit("image-updated", { imageId });
    }

    dropzoneUploadError(file: any, message: any, _xhr: XMLHttpRequest) {
        const dropzone: any = this.$refs.myVueDropzone;
        // noinspection JSUnresolvedReference
        dropzone?.removeFile(file);

        let detail = typeof message === "string" ? message : message?.detail || message?.title || message?.message;
        if (detail && (detail.length > 300 || detail.trimStart().startsWith("<"))) detail = undefined;
        this.$bvModal.msgBoxOk(detail || "The image could not be uploaded. Please try again.", { title: "Upload failed" });
    }

    // ---- Annotation save ----------------------------------------------------------------------

    async onPdfAnnotationSaved(payload: {
        oldImageId: number | null;
        newImageId: number;
        escriptId: number | null;
        saveToAll: boolean;
        orphanUpdates?: Array<{ storeID: number; rxNumber: number; rfNumber: number; newImageId: number }>;
    }) {
        if (!this.prescription) return;

        // Clear the quick annotate state; the container clears the note textarea (unless "keep note").
        if (this.isQuickAnnotate) {
            this.$emit("note-consumed");
            this.quickAnnotateText = null;
            this.isQuickAnnotate = false;
        }

        // Handle eScript annotation - just refresh the display, no prescription update needed
        if (payload.escriptId) {
            // Refresh the eScript image display
            if (this.imageComponent) {
                (this.imageComponent as any).fetchPrescriptionImageSource(`escript/${payload.escriptId}/image`);
            }
            this.$notification(NotificationOptions.success("eScript annotation saved successfully."));
            return;
        }

        // Handle regular image annotation - update ONLY the image ID
        if (payload.newImageId) {
            try {
                if (payload.saveToAll && payload.oldImageId) {
                    await PrescriptionService.updateSiblingImages(payload.oldImageId, payload.newImageId);

                    const count = this.siblingScriptCount;
                    this.$notification(NotificationOptions.success(
                        `Annotated PDF saved and applied to ${count} prescription${count !== 1 ? 's' : ''}.`
                    ));

                    // Update individually-annotated prescriptions with re-annotated images
                    if (payload.orphanUpdates?.length) {
                        try {
                            for (const update of payload.orphanUpdates) {
                                await PrescriptionService.updateImage({
                                    storeID: update.storeID,
                                    rxNumber: update.rxNumber,
                                    rfNumber: update.rfNumber,
                                    newImageId: update.newImageId,
                                });
                            }
                        } catch (orphanErr) {
                            console.error("Failed to update some orphaned siblings:", orphanErr);
                        }
                    }
                } else {
                    // Only an already-saved Rx row can be re-pointed; pre-save links via the in-memory id at first save
                    const linkedToRx = !!this.prescription.rxNumber && !this.newRefillMode;
                    if (linkedToRx) {
                        await PrescriptionService.updateImage({
                            storeID: this.prescription.storeID,
                            rxNumber: this.prescription.rxNumber,
                            rfNumber: this.prescription.rfNumber,
                            newImageId: payload.newImageId,
                        });
                    }

                    this.$notification(NotificationOptions.success(
                        linkedToRx
                            ? "Annotated PDF saved and linked to prescription."
                            : "Annotated image saved. It will be attached when you save the prescription."
                    ));
                }

                // The container owns entity mutation; emit and let it reassign prescription.imageID.
                this.$emit("image-updated", { imageId: payload.newImageId });

                this.refreshImage(payload.newImageId);
            } catch (err: any) {
                console.error("Failed to update prescription with new image:", err);
                this.$notification(NotificationOptions.error(err?.response?.data || "Failed to update prescription with annotated image."));
            }
        }
    }

    onPdfAnnotationClosed() {
        // Reset quick annotate state when modal is closed
        this.quickAnnotateText = null;
        this.isQuickAnnotate = false;
    }
}
</script>

<style lang="scss" scoped>
#prescription-image {
    min-height: 880px;
}

.image-wrapper {
    position: sticky;
    top: 0;
}

.script-image {
    height: 100vh;
    border: 1px solid black;
}
</style>
